'use client';

import { useMemo, type ReactNode } from 'react';
import { ConvexReactClient } from 'convex/react';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { ConvexAuthNextjsProvider } from '@convex-dev/auth/nextjs';
import { ClerkProvider, useAuth as useClerkAuth } from '@clerk/nextjs';
import { convexUrl, isLocalAuth } from '@/lib/authMode';
import { ClerkSessionBridge, LocalSessionBridge } from './session';

// ============================================================================
// LA RADICE DELL'AUTENTICAZIONE.
//
// Un solo punto in tutta l'app in cui si decide chi tiene l'identita'. Sopra
// c'e' il layout, sotto c'e' l'app: nessuno dei due sa niente di Clerk o di
// Convex Auth, e questo e' esattamente il motivo per cui lo stesso codice puo'
// girare sui nostri server e su quello del cliente.
//
// In tutti e due i casi il client Convex e' lo stesso oggetto: cambia solo chi
// gli passa il token, e a quale indirizzo punta.
// ============================================================================

// Un client solo per tutta la vita della pagina: ricrearlo a ogni render
// vorrebbe dire chiudere e riaprire la connessione, e perdere le sottoscrizioni
// che tengono aggiornate le schermate.
let cached: { url: string; client: ConvexReactClient } | null = null;

function convexClient(url: string): ConvexReactClient {
  if (!cached || cached.url !== url) {
    cached = { url, client: new ConvexReactClient(url) };
  }
  return cached.client;
}

export function AuthRoot({ children }: { children: ReactNode }) {
  const local = isLocalAuth();
  const client = useMemo(() => convexClient(convexUrl()), []);

  if (local) {
    // Installazione del cliente: login, sessioni e token stanno dentro Convex,
    // quindi dentro la loro macchina. Nessuna chiamata verso l'esterno.
    return (
      <ConvexAuthNextjsProvider client={client}>
        <LocalSessionBridge>{children}</LocalSessionBridge>
      </ConvexAuthNextjsProvider>
    );
  }

  // Cloud: Clerk firma il token, ConvexProviderWithClerk lo consegna a Convex a
  // ogni chiamata, e le funzioni server vedono ctx.auth.getUserIdentity().
  return (
    <ClerkProvider>
      <ConvexProviderWithClerk client={client} useAuth={useClerkAuth}>
        <ClerkSessionBridge>{children}</ClerkSessionBridge>
      </ConvexProviderWithClerk>
    </ClerkProvider>
  );
}
